import {
  useCallback,
  useLayoutEffect,
  useState,
  type RefObject,
} from "react";

const GAP = 4;
const VIEW_PAD = 8;

/**
 * Viewport-fixed top/left for content portaled to document.body, anchored to a trigger element.
 */
export function useAnchoredPortalPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  width: number,
  heightEstimate: number
): { top: number; left: number } | null {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    if (!open || !anchor) {
      setPos(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const height = contentRef.current?.offsetHeight ?? heightEstimate;

    let left = rect.right - width;
    left = Math.min(Math.max(left, VIEW_PAD), vw - width - VIEW_PAD);

    let top = rect.bottom + GAP;
    if (top + height > vh - VIEW_PAD) {
      const aboveTop = rect.top - GAP - height;
      if (aboveTop >= VIEW_PAD) {
        top = aboveTop;
      } else {
        top = Math.max(VIEW_PAD, vh - height - VIEW_PAD);
      }
    }

    setPos({ top, left });
  }, [open, anchorRef, contentRef, width, heightEstimate]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    update();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(update);
    });
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, update]);

  return open ? pos : null;
}
